import React, { FC } from 'react';
import { Document } from '@contentful/rich-text-types';
import classNames from 'classnames';
import { RichText } from '../RichText';
import { LuvImage } from '@/components/ui';
import { DynamicComponent } from '../DynamicComponent';
import { TypeExplorerSliceFields } from '@/core/generated-types';

const variantStyles = {
  center: {
    textAlign: 'text-center',
    wrapperComponents: 'grid justify-center',
    sliceText: 'justify-items-center',
  },
  left: {
    textAlign: 'text-center lg:text-left',
    wrapperComponents: '',
    sliceText: 'justify-between',
  },
  twoColumnGrid: {
    textAlign: 'text-center',
    wrapperComponents:
      'mx-auto grid grid-cols-1 lg:grid-cols-2 justify-items-center gap-y-[32px] gap-x-[25px] max-w-[800px] mt-6',
    sliceText: 'justify-items-center',
  },
};

const Divider = () => (
  <hr className="w-full border-t border-grey-mid-light mb-6 md:mb-16" />
);

const ExplorerSlice: FC<TypeExplorerSliceFields> = ({
  anchorId,
  decorativeImage,
  title,
  textLeft,
  textRight,
  components,
  variant,
  displaySliceDivider,
}) => {
  const hasSliceText = textLeft || textRight;
  const hasVariant = variant && variantStyles[variant];
  return (
    <div
      data-ga-section="explorer-section"
      data-testid="explorer-section"
      className={`mb-6 lg:container md:mb-16 lg:w-full scroll-mt-24 lg:scroll-mt-32
        ${hasVariant ? variantStyles[variant].textAlign : ''}
      `}
      id={anchorId}
    >
      {decorativeImage && (
        <div className="flex justify-center lg:block">
          <LuvImage imageData={decorativeImage} className="" />
        </div>
      )}
      {displaySliceDivider && <Divider />}
      {title && (
        <h3 className="text-left mb-4 mt-6 font-bold text-h-x4 lg:text-h-x3">
          {title}
        </h3>
      )}
      {hasSliceText && (
        <div
          className={classNames(
            'lg:grid lg:grid-cols-explorer-slice lg:gap-x-12',
            hasVariant ? variantStyles[variant].sliceText : '',
          )}
        >
          {textLeft && (
            <div
              className={`mb-4 mt-4 text-d-base lg:mt-0
            ${textRight ? 'lg:max-w-[640px]' : 'mb-24 pl-6 lg:max-w-[740px]'}`}
            >
              <RichText
                content={textLeft as Document}
                addParagraphMargins={false}
              />
            </div>
          )}
          {textRight && (
            <div className="mb-4 mt-4 text-d-base lg:mt-0 lg:max-w-[360px]">
              <RichText
                content={textRight as Document}
                addParagraphMargins={false}
              />
            </div>
          )}
        </div>
      )}
      <div
        data-testid="components-list"
        className={`${hasVariant ? variantStyles[variant].wrapperComponents : ''}`}
      >
        {components &&
          components.map((component: any, i: number) => (
            <DynamicComponent
              key={`${component.sys.id}-${i}`}
              component={component}
              data-list-id={anchorId.replaceAll('–', '-')}
              data-list-name={title}
              removeContainer
            />
          ))}
      </div>
    </div>
  );
};

export default React.memo(ExplorerSlice);
